import { nFormatter } from "@asayinc/component-library";
import { Box } from "@mui/material";
import { useParams } from "react-router-dom";

import { Card } from "src/components/atoms/Card";
import { ErrorState } from "src/components/atoms/ErrorState";
import { ScrollContainer } from "src/components/containers/ScrollContainer";
import { LabelField } from "src/components/molecules/LabelField";
import { LoadingCard } from "src/components/molecules/Loading";
import { useBankruptcyEventQuery } from "src/store/bankruptcyEvent";
import { getPercentage } from "src/utils/getPercentage";

export function BankruptcyEventStatsTab() {
  const { id } = useParams() as { id: string };
  const {
    data: bankruptcyEvent,
    isLoading,
    error,
  } = useBankruptcyEventQuery({ id });

  if (error || (!isLoading && !bankruptcyEvent)) {
    return <ErrorState />;
  }

  if (isLoading) {
    return (
      <ScrollContainer sx={{ pt: 8, pb: 10, pl: 10, pr: 6.25 }}>
        <Box sx={{ display: "flex" }}>
          <Box sx={{ flexGrow: 1, flexBasis: 0, mr: 12 }}>
            <LoadingCard />
          </Box>
          <Box sx={{ flexGrow: 1, flexBasis: 0 }}>
            <LoadingCard />
          </Box>
        </Box>
      </ScrollContainer>
    );
  }

  const { bankruptcyEventStats: stats } = bankruptcyEvent;

  const {
    totalPositions,
    totalCustomers,
    totalShares,
    giftedPositions,
    nonFullSharePositions,
    fullSharePositions,
    invoiceDate,
    invoiceAmount,
    estimatedInvoiceAmount,
  } = stats;

  return (
    <ScrollContainer sx={{ pt: 8, pb: 10, pl: 10, pr: 6.25 }}>
      <Box sx={{ display: "flex" }}>
        <Box sx={{ flexGrow: 1, flexBasis: 0, mr: 12 }}>
          <Card title="Position breakdown">
            <LabelField
              sx={{
                py: 5,
                px: 4,
                borderBottom: "1px solid #e0e0e0",
                fontWeight: 600,
              }}
              isLabelBold={true}
              label="Total shares owned across all customers"
              value={`${nFormatter(Number(totalShares), true)} shares`}
              valueTooltip={`${Number(totalShares).toLocaleString()} shares`}
            />
            <LabelField
              sx={{
                py: 5,
                px: 4,
                borderBottom: "1px solid #e0e0e0",
              }}
              label="Total number of positions"
              isLabelBold={true}
              value={`${totalPositions.toLocaleString()} positions`}
            />
            <LabelField
              sx={{
                py: 5,
                px: 4,
                borderBottom: "1px solid #e0e0e0",
              }}
              label="Total number of customers"
              isLabelBold={true}
              value={`${totalCustomers.toLocaleString()} customers`}
            />
            <LabelField
              sx={{
                py: 5,
                pr: 4,
                pl: 4,
                ml: 9,
                borderBottom: "1px solid #e0e0e0",
              }}
              isValueBold={false}
              label="Number of customers that have full share positions"
              tooltip="The number of customers that owned equal to or greater than one full share of the security as of the record date."
              value={`${fullSharePositions.toLocaleString()} (${getPercentage(
                fullSharePositions,
                totalCustomers
              )}%)`}
            />
            <LabelField
              sx={{
                py: 5,
                pr: 4,
                pl: 4,
                ml: 9,
                borderBottom: "1px solid #e0e0e0",
              }}
              isValueBold={false}
              label="Number of customers that have fractional positions"
              tooltip="The number of customers that owned less than one full share of the security as of the record date."
              value={`${nonFullSharePositions.toLocaleString()} (${getPercentage(
                nonFullSharePositions,
                totalCustomers
              )}%)`}
            />
            <LabelField
              sx={{
                py: 5,
                pr: 4,
                pl: 4,
                ml: 9,
                borderBottom: "1px solid #e0e0e0",
              }}
              isValueBold={false}
              label="Number of customers that have gifted positions"
              tooltip="The number of customers whose positions have been fully gifted to their account as a result of a brokerage promotion or reward."
              value={`${giftedPositions.toLocaleString()} (${getPercentage(
                giftedPositions,
                totalCustomers
              )}%)`}
            />
          </Card>
        </Box>
        <Box sx={{ flexGrow: 1, flexBasis: 0 }}>
          <Card title="Invoice information">
            <LabelField
              sx={{
                py: 5,
                px: 4,
                borderBottom: "1px solid #e0e0e0",
              }}
              isLabelBold={true}
              label="Invoice date"
              value={invoiceDate || "N/A"}
            />
            <LabelField
              sx={{
                py: 5,
                px: 4,
                borderBottom: "1px solid #e0e0e0",
              }}
              isLabelBold={true}
              label="Invoice amount"
              value={`$${parseFloat(invoiceAmount).toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}`}
            />
            <LabelField
              sx={{
                py: 5,
                px: 4,
                borderBottom: "1px solid #e0e0e0",
              }}
              isLabelBold={true}
              label="Estimated invoice amount"
              value={`$${parseFloat(estimatedInvoiceAmount).toLocaleString(
                undefined,
                {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                }
              )}`}
            />
          </Card>
        </Box>
      </Box>
    </ScrollContainer>
  );
}
